import apiClient from '../utils/api';

export type LiteratureCase = {
  id: number;
  company_id: number;
  created_by: number;
  article_id: number;
  source?: string | null;
  ai_score?: number | null;
  ai_summary?: string | null;
  status: string;
  final_verdict?: string | null;
  created_at: string;
  updated_at: string;
  
  // Patient Information
  patient_identifier?: string | null;
  patient_age?: string | null;
  patient_sex?: string | null;
  patient_weight?: string | null;
  patient_height?: string | null;
  pregnancy_status?: string | null;
  
  // Adverse Event Details
  event_verbatim?: string | null;
  meddra_pt?: string | null;
  ae_start_date?: string | null;
  ae_stop_date?: string | null;
  ae_duration?: string | null;
  outcome?: string | null;
  seriousness?: string | null;
  severity?: string | null;
  causality?: string | null;
  dechallenge?: string | null;
  rechallenge?: string | null;
  
  // Suspect Product Information
  suspect_product_name?: string | null;
  suspect_product_generic?: string | null;
  dosage_form?: string | null;
  strength?: string | null;
  indication?: string | null;
  route?: string | null;
  dose?: string | null;
  product_start_date?: string | null;
  product_stop_date?: string | null;
  batch_number?: string | null;
  expiry_date?: string | null;
  action_taken?: string | null;
  
  // Concomitant Information
  concomitants?: string | null;
  medical_history?: string | null;
  labs?: string | null;
  risk_factors?: string | null;
  
  // Reporter Information
  reporter_name?: string | null;
  reporter_type?: string | null;
  reporter_occupation?: string | null;
  reporter_contact?: string | null;
  reporter_country?: string | null;
  reporter_relationship?: string | null;
  
  // Manufacturer Information
  manufacturer_name?: string | null;
  manufacturer_contact?: string | null;
  
  // Case Narrative and Processing
  case_narrative?: string | null;
  assessor_comments?: string | null;
  case_type?: string | null;
  primary_source_country?: string | null;
  received_date?: string | null;
  reported_to_authority_date?: string | null;
  expedited?: boolean | null;
  follow_up_needed?: boolean | null;

  // Optional article context returned by list_cases
  article_title?: string | null;
  article_venue?: string | null;
  article_year?: number | null;
};

export async function listCases() {
  const { data, error, status } = await apiClient.get<{ cases: LiteratureCase[] }>('/api/literature/cases');
  if (!data) throw new Error(error || `Failed to load cases (HTTP ${status})`);
  return data.cases || [];
}

export async function createCasesFromResults(results: any[]) {
  const { data, error, status } = await apiClient.post<{ created_ids: number[] }>('/api/literature/cases', { results });
  if (!data) throw new Error(error || `Failed to create cases (HTTP ${status})`);
  return data.created_ids || [];
}

export async function updateCase(caseId: number, payload: Record<string, any>) {
  const { data, error, status } = await apiClient.post<{ ok: boolean }>(`/api/literature/cases/${caseId}`, payload);
  if (!data) throw new Error(error || `Failed to update case (HTTP ${status})`);
  return data.ok;
}

export async function exportCase(caseId: number) {
  const { data, error, status } = await apiClient.post(`/api/literature/cases/${caseId}/export`, {});
  if (!data) throw new Error(error || `Failed to export case (HTTP ${status})`);
  return data;
}

export async function deleteCase(caseId: number) {
  const { data, error, status } = await apiClient.delete(`/api/literature/cases/${caseId}`);
  if (!data) throw new Error(error || `Failed to delete case (HTTP ${status})`);
  return (data as any).ok === true;
}

export async function getCaseDetail(caseId: number) {
  const { data, error, status } = await apiClient.get(`/api/literature/cases/${caseId}/detail`);
  if (!data) throw new Error(error || `Failed to load case detail (HTTP ${status})`);
  return data as any;
}

export async function listCaseComments(caseId: number) {
  const { data, error, status } = await apiClient.get(`/api/literature/cases/${caseId}/comments`);
  if (!data) throw new Error(error || `Failed to load comments (HTTP ${status})`);
  return data as Array<{ id: number; user_id: number; user_name: string; comment_text: string; created_at: string }>;
}

export async function addCaseComment(caseId: number, commentText: string) {
  const { data, error, status } = await apiClient.post(`/api/literature/cases/${caseId}/comments`, { comment_text: commentText });
  if (!data) throw new Error(error || `Failed to add comment (HTTP ${status})`);
  return data as { id: number; user_id: number; user_name: string; comment_text: string; created_at: string };
}


